import type { MessageTypeV3Condition as MessageTypeV3ConditionType } from "@redotech/redo-flows-common/rules-v3/schemas";
import {
  MESSAGE_TYPE_CHANNELS_V3,
  type MessageTypeChannelV3,
} from "@redotech/redo-flows-common/rules-v3/types";
import { t, useTranslation } from "@redotech/redo-i18n";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";

const messageTypeDisplayNames = (): Record<MessageTypeChannelV3, string> => ({
  gmail: "Gmail",
  outlook: "Outlook",
  customEmail: t("Custom Email"),
  redoChat: t("Chat"),
  facebook: "Facebook",
  facebookComments: t("{{integrationName}} Comments", {
    integrationName: "Facebook",
  }),
  instagram: "Instagram",
  instagramComments: t("{{integrationName}} Comments", {
    integrationName: "Instagram",
  }),
  sms: t("SMS"),
  voice: t("Voice"),
  attentive: "Attentive",
  postscript: "Postscript",
  tiktok: "TikTok",
  contactForm: t("Contact Form"),
  internalNote: t("Internal Note"),
});

const messageTypeItems = (): RedoListItem<MessageTypeChannelV3>[] =>
  MESSAGE_TYPE_CHANNELS_V3.map((channel) => ({
    id: channel,
    type: "text" as const,
    text: messageTypeDisplayNames()[channel],
    value: channel,
  }));

export const MessageTypeV3Condition = memo(function MessageTypeV3Condition({
  condition,
  onChange,
  editableFields,
}: {
  condition: MessageTypeV3ConditionType;
  onChange: (updated: MessageTypeV3ConditionType) => void;
  editableFields?: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const messageTypesEditable =
    editableFields === undefined || editableFields.has("messageTypes");

  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const selectedItems: RedoListItem<MessageTypeChannelV3>[] = useMemo(() => {
    return (condition.messageTypes || []).map((type) => ({
      id: type,
      type: "text" as const,
      text: messageTypeDisplayNames()[type],
      value: type,
    }));
  }, [condition.messageTypes]);

  const displayItem: RedoListItem<MessageTypeChannelV3> | undefined =
    useMemo(() => {
      if (selectedItems.length === 0) {
        return undefined;
      }
      if (selectedItems.length === 1) {
        return selectedItems[0];
      }
      return {
        id: "display",
        type: "text" as const,
        text: selectedItems
          .flatMap((item) => ("text" in item ? [item.text] : []))
          .join(", "),
        value: "gmail" as MessageTypeChannelV3,
      };
    }, [selectedItems]);

  return (
    <Flex dir="column" gap="sm" w="full">
      <Text fontSize="xs" textColor="secondary">
        {t("Message type")}
      </Text>
      <RedoSingleSelectDropdownInputButton
        disabled={!messageTypesEditable}
        isDropdownOpen={dropdownOpen}
        onClick={() => messageTypesEditable && setDropdownOpen(!dropdownOpen)}
        placeholder={t("Select message types")}
        ref={setDropdownButtonRef}
        selectedItem={displayItem}
        size="xs"
      />
      <RedoMultiselectDropdown
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        fitToAnchor
        items={messageTypeItems()}
        selectedItems={selectedItems}
        selectionVariant="checkbox"
        setDropdownOpen={setDropdownOpen}
        setSelectedItems={(items) => {
          if (!messageTypesEditable) {
            return;
          }
          onChange({
            ...condition,
            messageTypes: items.map((item) => item.value),
          });
        }}
        size="xs"
      />
    </Flex>
  );
});
